import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, signUp } from '../lib/supabase';

export default function AuthPage() {
  const [mode, setMode]       = useState('login'); // 'login' | 'signup'
  const [email, setEmail]     = useState('');
  const [password, setPass]   = useState('');
  const [name, setName]       = useState('');
  const [company, setCompany] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
        navigate('/dashboard');
      } else {
        if (!name.trim()) { setError('Full name is required'); setLoading(false); return; }
        await signUp(email, password, name.trim(), company.trim());
        setError('');
        // Show confirmation message
        setMode('confirm');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    }
    setLoading(false);
  }

  if (mode === 'confirm') return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>PMD</div>
        <h2 style={S.h2}>Check your email</h2>
        <p style={{color:V.muted,fontSize:14,textAlign:'center',lineHeight:1.6}}>
          We sent a confirmation link to <strong style={{color:V.text}}>{email}</strong>.<br/>
          Click it to activate your account, then come back to sign in.
        </p>
        <button style={{...S.btn,marginTop:24}} onClick={()=>setMode('login')}>Back to Sign In</button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.left}>
        <div style={S.brand}>
          <div style={S.logoLg}>PMD</div>
          <div style={{marginTop:24}}>
            <div style={S.brandName}>ProcessMD</div>
            <div style={S.tagline}>AI Diagnostics for Your Operations</div>
          </div>
        </div>
        <div style={S.featureList}>
          {['Upload any process event log', 'Run 10 analytics instantly', 'AI-generated Word reports', 'Branded PowerPoint decks', 'Secure · Private · Fast'].map((f,i)=>(
            <div key={i} style={S.feature}>
              <span style={S.featureDot}/>
              <span>{f}</span>
            </div>
          ))}
        </div>
        <div style={S.leftBottom}>
            <div style={{marginBottom:8}}>&copy; {new Date().getFullYear()} ProcessMD &mdash; AI Diagnostics for Your Operations</div>
            <a href="/onepager.html" target="_blank" style={{color:'#3b82f6',fontSize:11,marginBottom:8,display:'inline-flex',alignItems:'center',gap:5,textDecoration:'none'}}>📄 Download product one-pager</a>
            <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
              <a href="/legal.html" target="_blank" style={{color:'#475569',fontSize:11,textDecoration:'none'}}>Terms of Service</a>
              <a href="/legal.html#privacy" target="_blank" style={{color:'#475569',fontSize:11,textDecoration:'none'}}>Privacy Policy</a>
              <a href="/legal.html#disclaimer" target="_blank" style={{color:'#475569',fontSize:11,textDecoration:'none'}}>Disclaimer</a>
            </div>
          </div>
      </div>

      <div style={S.right}>
        <div style={S.card}>
          <div style={S.logo}>PMD</div>
          <h1 style={S.h1}>{mode==='login' ? 'Welcome back' : 'Create account'}</h1>
          <p style={S.sub}>{mode==='login' ? 'Sign in to your workspace' : 'Start analysing your processes today'}</p>

          <form onSubmit={handleSubmit} style={{width:'100%'}}>
            {mode==='signup' && <>
              <label style={S.label}>Full name</label>
              <input style={S.input} type="text" placeholder="Jane Smith" value={name} onChange={e=>setName(e.target.value)} required/>
              <label style={S.label}>Company <span style={{color:V.muted}}>(optional)</span></label>
              <input style={S.input} type="text" placeholder="Acme Corp" value={company} onChange={e=>setCompany(e.target.value)}/>
            </>}
            <label style={S.label}>Email address</label>
            <input style={S.input} type="email" placeholder="jane@company.com" value={email} onChange={e=>setEmail(e.target.value)} required/>
            <label style={S.label}>Password</label>
            <input style={S.input} type="password" placeholder={mode==='signup'?'At least 8 characters':'••••••••'} value={password} onChange={e=>setPass(e.target.value)} required minLength={8}/>

            {error && <div style={S.error}>{error}</div>}

            <button style={{...S.btn, opacity:loading?0.6:1}} type="submit" disabled={loading}>
              {loading ? 'Please wait…' : mode==='login' ? 'Sign in' : 'Create account'}
            </button>
          </form>

          <div style={S.switch}>
            {mode==='login' ? <>Don't have an account? <button style={S.link} onClick={()=>{setMode('signup');setError('');}}>Sign up free</button></> : <>Already have an account? <button style={S.link} onClick={()=>{setMode('login');setError('');}}>Sign in</button></>}
          </div>
        </div>
      </div>
      {/* Floating video widget */}
      <VideoWidget/>
    </div>
  );
}

function VideoWidget() {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Appear after 3 seconds
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);

  if (dismissed) return null;

  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:1000,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease',
      fontFamily:"'DM Sans',system-ui,sans-serif",
    }}>
      {open ? (
        // Expanded video player
        <div style={{
          width:340, background:'#0a1525', border:'1px solid #1e2d4a',
          borderRadius:12, overflow:'hidden',
          boxShadow:'0 25px 50px rgba(0,0,0,0.6)',
        }}>
          {/* Header */}
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:'1px solid #1e2d4a'}}>
            <div>
              <div style={{fontSize:12, fontWeight:700, color:'#e2e8f0'}}>ProcessMD in action</div>
              <div style={{fontSize:10, color:'#64748b', marginTop:1}}>See how it works</div>
            </div>
            <button onClick={()=>setOpen(false)} style={{background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:16, padding:4, lineHeight:1}}>✕</button>
          </div>
          {/* Video */}
          <div style={{position:'relative', paddingBottom:'56.25%', height:0}}>
            <iframe
              src="https://www.youtube.com/embed/Yr7O08jbNZQ?autoplay=1"
              title="ProcessMD — See how it works"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
            />
          </div>
          {/* Footer */}
          <div style={{padding:'10px 14px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:10, color:'#64748b'}}>tryprocessmd.com</span>
            <button onClick={()=>setDismissed(true)} style={{background:'none', border:'none', color:'#475569', cursor:'pointer', fontSize:10}}>Dismiss</button>
          </div>
        </div>
      ) : (
        // Collapsed pill
        <div style={{display:'flex', alignItems:'center', gap:0, filter:'drop-shadow(0 8px 24px rgba(0,0,0,0.5))'}}>
          <button
            onClick={() => setOpen(true)}
            style={{
              display:'flex', alignItems:'center', gap:10,
              background:'#0D9488', border:'none', borderRadius:'28px 0 0 28px',
              padding:'10px 18px 10px 14px', cursor:'pointer',
              color:'#fff',
            }}
          >
            <div style={{
              width:28, height:28, borderRadius:'50%',
              background:'rgba(255,255,255,0.2)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, flexShrink:0,
            }}>▶</div>
            <div style={{textAlign:'left'}}>
              <div style={{fontSize:12, fontWeight:700, color:'#fff', whiteSpace:'nowrap'}}>Watch a quick overview</div>
              <div style={{fontSize:10, color:'rgba(255,255,255,0.7)', marginTop:1}}>See ProcessMD in action</div>
            </div>
          </button>
          <button
            onClick={() => setDismissed(true)}
            style={{
              background:'#0a1525', border:'1px solid #1e2d4a', borderLeft:'none',
              borderRadius:'0 28px 28px 0', padding:'10px 12px',
              color:'#475569', cursor:'pointer', fontSize:13, lineHeight:1,
            }}
          >✕</button>
        </div>
      )}
    </div>
  );
}

const V = { bg:'#040c18', panel:'#0a1525', border:'#1e2d4a', text:'#e2e8f0', muted:'#64748b', accent:'#3b82f6', gold:'#D4A017', navy:'#0D1B3E' };

const S = {
  page:      { display:'flex', minHeight:'100vh', background:V.bg, fontFamily:"'DM Sans',system-ui,sans-serif", color:V.text },
  left:      { width:'45%', background:'linear-gradient(135deg,#0D1B3E 0%,#061025 100%)', padding:'60px 48px', display:'flex', flexDirection:'column', justifyContent:'space-between', borderRight:`1px solid ${V.border}` },
  right:     { flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:40 },
  brand:     { display:'flex', flexDirection:'column' },
  logoLg:    { width:64, height:64, background:V.gold, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, fontWeight:800, color:V.navy, fontFamily:"'IBM Plex Mono',monospace" },
  brandName: { fontSize:28, fontWeight:800, color:'#fff', letterSpacing:'-0.02em', marginBottom:4 },
  tagline:   { fontSize:13, color:'#8BA3C7', fontStyle:'italic' },
  featureList:{ display:'flex', flexDirection:'column', gap:16 },
  feature:   { display:'flex', alignItems:'center', gap:12, fontSize:15, color:'#94a3b8' },
  featureDot:{ width:6, height:6, borderRadius:'50%', background:V.gold, flexShrink:0 },
  leftBottom:{ fontSize:12, color:'#334155' }, onepager:{ fontSize:11, color:'#3b82f6', cursor:'pointer', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6, marginTop:12 },
  card:      { background:V.panel, border:`1px solid ${V.border}`, borderRadius:16, padding:'40px 40px', width:'100%', maxWidth:420, display:'flex', flexDirection:'column', alignItems:'center' },
  logo:      { width:44, height:44, background:V.gold, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:V.navy, fontFamily:"'IBM Plex Mono',monospace", marginBottom:20 },
  h1:        { fontSize:24, fontWeight:700, margin:'0 0 6px', letterSpacing:'-0.02em' },
  h2:        { fontSize:22, fontWeight:700, margin:'0 0 12px' },
  sub:       { fontSize:14, color:V.muted, margin:'0 0 28px' },
  label:     { display:'block', fontSize:12, fontWeight:600, color:'#94a3b8', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:6, width:'100%' },
  input:     { display:'block', width:'100%', background:'#070f1c', border:`1px solid ${V.border}`, borderRadius:8, padding:'11px 14px', fontSize:14, color:V.text, marginBottom:16, outline:'none', boxSizing:'border-box', fontFamily:"'DM Sans',system-ui,sans-serif" },
  btn:       { width:'100%', background:V.accent, color:'#fff', border:'none', borderRadius:8, padding:'13px 0', fontSize:14, fontWeight:700, cursor:'pointer', marginTop:4, fontFamily:"'DM Sans',system-ui,sans-serif" },
  error:     { background:'#450a0a', border:'1px solid #7f1d1d', borderRadius:8, padding:'10px 14px', color:'#fca5a5', fontSize:12, marginBottom:12, width:'100%', boxSizing:'border-box' },
  switch:    { marginTop:24, fontSize:13, color:V.muted },
  link:      { background:'none', border:'none', color:V.accent, cursor:'pointer', fontWeight:600, fontSize:13 },
};
